import { Injectable, Injector, effect, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';

import { RouteStateService } from '../route-state/route-state.service';

/**
 * S-1. Keep the two unauthenticated, person-identifying pages out of search
 * results.
 *
 * `/p/:slug` (the published portfolio) and `/shared-report/:token` (the
 * counselor's read of a goals report) both render a real member's name and
 * history to anyone holding the link, and neither sits behind a guard --
 * deliberately, because the visitor is not the member and has no account here.
 * Nothing stopped a crawler indexing either of them: src/robots.txt was
 * declared as a build asset and did not exist, so /robots.txt served the SPA's
 * own index.html with a 200 and no rules at all.
 *
 * robots.txt is now present, and this is its other half. The two do different
 * jobs and both are needed:
 *
 *   robots.txt  asks a crawler not to FETCH the path. A crawler that ignores it
 *               fetches anyway, and a path that is merely disallowed can still
 *               appear in results as a bare URL when another site links to it.
 *   noindex     asks a crawler that HAS fetched the page not to index it, which
 *               is the only instruction that removes an already-indexed page.
 *
 * Neither is access control. These are shareable links by design; this is about
 * not turning a link a member chose to send one person into a public record.
 *
 * Implemented as an effect over the existing route signals rather than as new
 * route `data`, so there is exactly one list of "which routes are public" in
 * the codebase -- RouteStateService -- and adding a third cannot forget this.
 *
 * SPA caveat, stated because it limits what this achieves: the tag is written
 * after the app boots. A crawler that does not execute JavaScript sees only
 * index.html and never sees it. That crawler is also the one that obeys
 * robots.txt, which is why the pair is the answer rather than either alone.
 */
@Injectable({ providedIn: 'root' })
export class RobotsMetaService {
  private static readonly SELECTOR = "name='robots'";
  private static readonly CONTENT = 'noindex, nofollow';

  private readonly meta = inject(Meta);
  private readonly routeState = inject(RouteStateService);
  // init() is called from AppComponent.ngOnInit, which is NOT an injection
  // context, so the effect needs the injector handed to it explicitly.
  private readonly injector = inject(Injector);

  public init(): void {
    effect(() => {
      const isPersonPage =
        this.routeState.isPublicPageRoute() ||
        this.routeState.isSharedReportRoute();

      if (isPersonPage) {
        this.meta.updateTag(
          { name: 'robots', content: RobotsMetaService.CONTENT },
          RobotsMetaService.SELECTOR
        );
        return;
      }

      // Removed rather than set to `index, follow`: an explicit positive
      // instruction on every other page is noise, and leaving a stale noindex
      // behind after navigating away from a portfolio would quietly delist the
      // whole product.
      this.meta.removeTag(RobotsMetaService.SELECTOR);
    }, { injector: this.injector });
  }
}
